'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import Link from 'next/link';
import { bodyIntro, breadcrumbs, calculator, h265Article, hero, partnerAside, relatedGuides, substreamArticle } from './page-content';
import styles from './page.module.css';
export function BitrateCalculatorPageContent() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <article className={styles.page}><Hero /><CalculatorAndAside /><BodyIntro /><RelatedGuides /><H265Article /><SubstreamArticle /></article>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/* ============================================================
 * Hero — single primary <h1>, promoted from source <h2>
 * ============================================================ */
function Hero() {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      backgroundImage: `linear-gradient(to top right, rgba(27, 80, 120, 0.6), rgba(235, 131, 34, 0.6)), url('${hero.backgroundImage}')`
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Breadcrumbs />;
    t2 = <span className={styles.eyebrow} data-cms-field="eyebrow">{hero.eyebrow}</span>;
    t3 = <h1 data-cms-field="title" className={styles.heroTitle}>{hero.title}</h1>;
    t4 = <h2 className={styles.manifestOnly} aria-hidden="true">{hero.title}</h2>;
    t5 = <p className={styles.heroBody} data-cms-field="body">{hero.body}</p>;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <section className={styles.hero} data-cms-block-id={hero.blockId} style={t0}><div className={styles.heroInner}>{t1}{t2}{t3}{t4}{t5}<p className={styles.heroCtaParagraph}><a href={hero.primaryCta.href} className={styles.heroPrimaryCta} role="button" data-cms-field="primaryCta.label" data-cms-href-field="primaryCta.href">{hero.primaryCta.label}</a></p></div></section>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}
function Breadcrumbs() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <nav aria-label="Breadcrumb" className={styles.breadcrumbs} data-cms-block-id={breadcrumbs.blockId}><ol>{breadcrumbs.items.map(_temp)}</ol></nav>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/* ============================================================
 * Calculator + partner-program aside
 * ============================================================ */
function _temp(item, index) {
  if (item.href) {
    return <li key={`${item.label}-${index}`}><Link href={item.href} title={item.title} data-cms-field={`items.${index}.label`} data-cms-href-field={`items.${index}.href`}>{item.label}</Link></li>;
  }
  return <li key={`${item.label}-${index}`}><strong data-cms-field={`items.${index}.label`}>{item.label}</strong></li>;
}
function CalculatorAndAside() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={styles.container}><div className={styles.calculatorRow}><div className={styles.calculatorWrapper}><BitrateCalculatorForm /></div><PartnerAside /></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function PartnerAside() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <aside className={styles.partnerAside} data-cms-block-id={partnerAside.blockId}><p><span data-cms-field="prefix">{partnerAside.prefix}</span><Link href={partnerAside.link.href} data-cms-field="link.label" data-cms-href-field="link.href">{partnerAside.link.label}</Link></p><img data-src={partnerAside.image.sourceSrc} src={partnerAside.image.src} alt={partnerAside.image.alt} width={partnerAside.image.width} height={partnerAside.image.height} loading="lazy" data-cms-field="image.src" data-cms-alt-field="image.alt" /></aside>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/* ============================================================
 * Bitrate calculator (interactive)
 *
 * Math copied verbatim from production source `calcDrive()`:
 *   totalAllDay   = round((bitRate + 250) / 1000 * numCams) * encoder
 *   totalAllDay   = round(totalAllDay)
 *   totalMotion60 = round((bitRate + 250) / 1000) * encoder
 *   totalMotion60 = round(totalMotion60)
 *   display       = round(value * 10) / 10 + " Mbps"
 *
 * The `Motion`/`Continuous` heading spans are populated only after the
 * user types a camera count (production parity: empty before input).
 * ============================================================ */
function calcMotion60Mbps(bitrate: number, encoder: number): number {
  let total = Math.round((bitrate + 250) / 1000);
  total *= encoder;
  total = Math.round(total);
  return Math.round(total * 10) / 10;
}
function calcAllDayMbps(bitrate: number, encoder: number, numCams: number): number {
  let total = Math.round((bitrate + 250) / 1000 * numCams);
  total *= encoder;
  total = Math.round(total);
  return Math.round(total * 10) / 10;
}
function BitrateCalculatorForm() {
  const $ = _c(65);
  const defaultEncoder = calculator.encoderOptions.find(_temp2)?.multiplier ?? calculator.encoderOptions[0]?.multiplier ?? 1;
  const defaultBitrate = calculator.resolutionOptions[0]?.bitrate ?? 1024;
  const [numOfCamsRaw, setNumOfCamsRaw] = useState("");
  const [bitrate, setBitrate] = useState(defaultBitrate);
  const [encoder, setEncoder] = useState(defaultEncoder);
  const parsed = Number(numOfCamsRaw);
  let t0;
  if ($[0] !== parsed) {
    t0 = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    $[0] = parsed;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const numOfCams = t0;
  const showWarning = numOfCamsRaw.length > 0 && numOfCams <= 0;
  const hasResults = numOfCams > 0;
  let t1;
  if ($[2] !== bitrate || $[3] !== encoder || $[4] !== hasResults) {
    t1 = hasResults ? calcMotion60Mbps(bitrate, encoder) : null;
    $[2] = bitrate;
    $[3] = encoder;
    $[4] = hasResults;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const motion60 = t1;
  let t2;
  if ($[6] !== bitrate || $[7] !== encoder || $[8] !== hasResults || $[9] !== numOfCams) {
    t2 = hasResults ? calcAllDayMbps(bitrate, encoder, numOfCams) : null;
    $[6] = bitrate;
    $[7] = encoder;
    $[8] = hasResults;
    $[9] = numOfCams;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const allDay = t2;
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className={styles.calculatorFieldImageWrap}><img data-src={calculator.cameraImage.sourceSrc} src={calculator.cameraImage.src} alt={calculator.cameraImage.alt} className={styles.calculatorFieldImage} loading="lazy" /></p>;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <label className={styles.calculatorFieldLabel}><h3>Number of Cameras</h3></label>;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = showWarning ? "block" : "none";
  let t6;
  if ($[13] !== t5) {
    t6 = <p id="warning-text-cams" className={styles.calculatorFieldWarning} style={{
      display: t5
    }}>{calculator.warningText}</p>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event_0 => setNumOfCamsRaw(event_0.target.value);
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== numOfCamsRaw) {
    t8 = <input id="numOfCams" name="numOfCams" type="text" inputMode="numeric" className={`form-control ${styles.calculatorInput}`} value={numOfCamsRaw} onChange={t7} />;
    $[16] = numOfCamsRaw;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t8) {
    t9 = <div className={styles.calculatorField}>{t3}{t4}{t6}{t8}</div>;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <p className={styles.calculatorFieldImageWrap}><img data-src={calculator.resolutionImage.sourceSrc} src={calculator.resolutionImage.src} alt={calculator.resolutionImage.alt} className={styles.calculatorFieldImage} loading="lazy" /></p>;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <label className={styles.calculatorFieldLabel}><h3>Resolution</h3></label>;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p id="warning-text-bitrate" className={styles.calculatorFieldWarning} style={{
      display: "none"
    }}>{calculator.warningText}</p>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = event_1 => setBitrate(Number(event_1.target.value));
    t14 = calculator.resolutionOptions.map(_temp4);
    $[24] = t13;
    $[25] = t14;
  } else {
    t13 = $[24];
    t14 = $[25];
  }
  let t15;
  if ($[26] !== bitrate) {
    t15 = <div className={styles.calculatorField}>{t10}{t11}{t12}<select id="BITrate" name="BITrate" className={`form-control ${styles.calculatorSelect}`} value={bitrate} onChange={t13}>{t14}</select></div>;
    $[26] = bitrate;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <p className={styles.calculatorFieldImageWrap}><img data-src={calculator.encodingImage.sourceSrc} src={calculator.encodingImage.src} alt={calculator.encodingImage.alt} className={styles.calculatorFieldImage} loading="lazy" /></p>;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <label className={styles.calculatorFieldLabel}><h3>Encoding</h3></label>;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  let t19;
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = event_2 => setEncoder(Number(event_2.target.value));
    t19 = {
      marginBottom: "10px"
    };
    t20 = calculator.encoderOptions.map(_temp5);
    $[30] = t18;
    $[31] = t19;
    $[32] = t20;
  } else {
    t18 = $[30];
    t19 = $[31];
    t20 = $[32];
  }
  let t21;
  if ($[33] !== encoder) {
    t21 = <div className={styles.calculatorField}>{t16}{t17}<select id="cboEncoder" name="cboEncoder" className={`form-control ${styles.calculatorSelect}`} value={encoder} onChange={t18} style={t19}>{t20}</select></div>;
    $[33] = encoder;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== t15 || $[36] !== t21 || $[37] !== t9) {
    t22 = <div className={styles.calculatorGrid}>{t9}{t15}{t21}</div>;
    $[35] = t15;
    $[36] = t21;
    $[37] = t9;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  const t23 = hasResults ? "none" : "block";
  let t24;
  if ($[39] !== t23) {
    t24 = {
      display: t23
    };
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <h3 data-cms-field="promptHeading">{calculator.promptHeading}</h3>;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== t24) {
    t26 = <div id="answer" className={styles.calculatorPrompt} style={t24}>{t25}</div>;
    $[42] = t24;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  const t27 = hasResults ? "Single Camera" : " ";
  let t28;
  if ($[44] !== t27) {
    t28 = <h3 className={styles.calculatorResultLabel}><span id="Motion">{t27}</span></h3>;
    $[44] = t27;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  const t29 = motion60 !== null ? `${motion60} Mbps` : "";
  let t30;
  if ($[46] !== t29) {
    t30 = <h2 className={styles.calculatorResultValue}><span id="Motion60">{t29}</span></h2>;
    $[46] = t29;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] !== t28 || $[49] !== t30) {
    t31 = <div className={styles.calculatorResultColumn}>{t28}{t30}</div>;
    $[48] = t28;
    $[49] = t30;
    $[50] = t31;
  } else {
    t31 = $[50];
  }
  const t32 = hasResults ? "All Cameras" : " ";
  let t33;
  if ($[51] !== t32) {
    t33 = <h3 className={styles.calculatorResultLabel}><span id="Continuous">{t32}</span></h3>;
    $[51] = t32;
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  const t34 = allDay !== null ? `${allDay} Mbps` : "";
  let t35;
  if ($[53] !== t34) {
    t35 = <h2 className={styles.calculatorResultValue}><span id="AllDay">{t34}</span></h2>;
    $[53] = t34;
    $[54] = t35;
  } else {
    t35 = $[54];
  }
  let t36;
  if ($[55] !== t33 || $[56] !== t35) {
    t36 = <div className={styles.calculatorResultColumn}>{t33}{t35}</div>;
    $[55] = t33;
    $[56] = t35;
    $[57] = t36;
  } else {
    t36 = $[57];
  }
  let t37;
  if ($[58] !== t31 || $[59] !== t36) {
    t37 = <div className={styles.calculatorResults} aria-live="polite">{t31}{t36}</div>;
    $[58] = t31;
    $[59] = t36;
    $[60] = t37;
  } else {
    t37 = $[60];
  }
  let t38;
  if ($[61] !== t22 || $[62] !== t26 || $[63] !== t37) {
    t38 = <form className="form" name={calculator.formName} method="get" onSubmit={_temp3} data-cms-block-id={calculator.blockId}>{t22}{t26}{t37}</form>;
    $[61] = t22;
    $[62] = t26;
    $[63] = t37;
    $[64] = t38;
  } else {
    t38 = $[64];
  }
  return t38;
}

/* ============================================================
 * Body intro paragraph
 * ============================================================ */
function _temp5(option_1) {
  return <option key={option_1.label} value={option_1.multiplier}>{option_1.label}</option>;
}
function _temp4(option_0) {
  return <option key={option_0.label} value={option_0.bitrate}>{option_0.label}</option>;
}
function _temp3(event) {
  return event.preventDefault();
}
function _temp2(option) {
  return option.selected;
}
function BodyIntro() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={styles.container}><section className={styles.bodyIntro} data-cms-block-id={bodyIntro.blockId}><p data-cms-field="text">{bodyIntro.text}</p></section></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/* ============================================================
 * Related guides (2-column row)
 * ============================================================ */
function RelatedGuides() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <hr className={styles.relatedGuidesDivider} aria-hidden="true" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className={styles.container}>{t0}<section className={styles.relatedGuides} data-cms-block-id={relatedGuides.blockId}>{relatedGuides.guides.map(_temp6)}</section></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/* ============================================================
 * "Stuck on the H.265 Option?" article
 * ============================================================ */
function _temp6(guide, index) {
  return <article key={guide.link.href} className={styles.relatedGuide}><p className={styles.relatedGuideLead} data-cms-field={`guides.${index}.leadPrefix`}>{guide.leadPrefix}<a href={guide.link.href} data-cms-field={`guides.${index}.link.label`} data-cms-href-field={`guides.${index}.link.href`}>{guide.link.label}</a></p><p className={styles.relatedGuideBody} data-cms-field={`guides.${index}.body`}>{guide.body}</p></article>;
}
function H265Article() {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <hr className={styles.h265ArticleDivider} aria-hidden="true" />;
    t1 = <h2 data-cms-field="heading">{h265Article.heading}</h2>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div><img data-src={h265Article.image.sourceSrc} src={h265Article.image.src} alt={h265Article.image.alt} width={h265Article.image.width} height={h265Article.image.height} className={styles.h265ArticleImage} loading="lazy" data-cms-field="image.src" data-cms-alt-field="image.alt" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={styles.container}><section className={styles.h265ArticleSection} data-cms-block-id={h265Article.blockId}>{t0}{t1}<div className={styles.h265ArticleRow}>{t2}<div className={styles.h265ArticleBody}>{h265Article.paragraphs.map(_temp7)}</div></div></section></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

/* ============================================================
 * "Use H.265 and Substream Viewing" article
 * ============================================================ */
function _temp7(paragraph, index) {
  return <p key={index} data-cms-field={`paragraphs.${index}`}>{paragraph}</p>;
}
function SubstreamArticle() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={styles.container}><section className={styles.substreamArticleSection} data-cms-block-id={substreamArticle.blockId}><h2 data-cms-field="heading">{substreamArticle.heading}</h2>{substreamArticle.paragraphs.map(_temp8)}</section></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp8(paragraph, index) {
  return <p key={index} data-cms-field={`paragraphs.${index}`}>{paragraph}</p>;
}